import { Surface } from "pages/HospitalsPage/components/BenefitSelection/Surface";
import { Button } from "primereact/button";
import { Checkbox } from "primereact/checkbox";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";
import { Dropdown } from "primereact/dropdown";
import { Message } from "primereact/message";
import { ProgressBar } from "primereact/progressbar";
import { SelectButton } from "primereact/selectbutton";
import { ReactNode, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useGetRemitAccuracySummaryQuery } from "store/queries/remitAccuracy";
import {
  IRemitAccuracyAccount,
  IRemitAccuracyFilters,
  IRemitAccuracyPayer,
  IRemitAiAgreementCell,
  IRemitAiDisagreementCluster,
  IRemitAiSummary,
  IRemitClusterRequest,
  IRemitHeadlineCell,
  IRemitMissCluster,
  IRemitNotConfiguredCallout,
  RemitAiAgreement,
  RemitPickKind,
  RemitScoredBenefit,
} from "types/RemitAccuracy/RemitAccuracy";

import { RemitAccuracyAiChecksDialog } from "./RemitAccuracyAiChecksDialog";
import { RemitAccuracyCluster } from "./RemitAccuracyCluster";
import { RemitAccuracyExcludedDialog } from "./RemitAccuracyExcludedDialog";
import {
  accuracyText,
  AI_AGREEMENT_LABELS,
  AI_AGREEMENT_ORDER,
  aiSkipReasonLabel,
  CONFIDENCE_BAND_LABELS,
  DATE_OF_SERVICE_OPTIONS,
  dateOfServiceRange,
  DateOfServicePreset,
  emptyStateText,
  exclusionLabel,
  formatRange,
  formatRemitValue,
  IRemitEmptyContext,
  percentOf,
  quotedIsAvailable,
  READ_FROM_LABELS,
  relativeTime,
  REMIT_BENEFIT_LABELS,
  REMIT_KIND_LABELS,
  remitShareWording,
  sortRemitRows,
  visibleExclusions,
  withMeasurement,
} from "./remitAccuracyWording";

const BENEFITS: RemitScoredBenefit[] = [
  "COINSURANCE_PERCENT",
  "FLAT_COPAY",
  "PROFESSIONAL_COPAY",
];
const KINDS: RemitPickKind[] = ["REPLAY", "QUOTED"];
const OTHER_KIND: Record<RemitPickKind, RemitPickKind> = {
  REPLAY: "QUOTED",
  QUOTED: "REPLAY",
};

/**
 * The tab's state in the URL, so a filtered view or an open cluster can be linked to. Filter changes REPLACE
 * the history entry; opening and closing a cluster PUSH one, so Back returns to the overview.
 */
const PARAM = {
  account: "account",
  payer: "payer",
  benefit: "benefit",
  dateOfService: "dos",
  pick: "pick",
  /** CLE-2820 — "Include AI comparison". Absent (unchecked) unless set to 1. */
  ai: "ai",
  clusterAccount: "clusterAccount",
  clusterPayer: "clusterPayer",
  clusterPlan: "clusterPlan",
  clusterNoPlan: "clusterNoPlan",
  clusterBenefit: "clusterBenefit",
  clusterReadFrom: "clusterReadFrom",
} as const;

const CLUSTER_PARAMS = [
  PARAM.clusterAccount,
  PARAM.clusterPayer,
  PARAM.clusterPlan,
  PARAM.clusterNoPlan,
  PARAM.clusterBenefit,
  PARAM.clusterReadFrom,
];

const oneOf = <T extends string>(value: string | null, allowed: T[]) =>
  value !== null && allowed.includes(value as T) ? (value as T) : undefined;

const cellFor = (
  cells: IRemitHeadlineCell[],
  kind: RemitPickKind,
  benefit: RemitScoredBenefit
) => cells.find((cell) => cell.kind === kind && cell.benefit === benefit);

/** Nothing at all for a kind under the current filters. */
const kindIsEmpty = (cells: IRemitHeadlineCell[], kind: RemitPickKind) =>
  cells
    .filter((cell) => cell.kind === kind)
    .every(
      (cell) =>
        cell.scored === 0 &&
        cell.notConfigured === 0 &&
        cell.remitsDisagree === 0
    );

interface ITableSort {
  sortField: string;
  sortOrder: 1 | -1;
}

/** Name ascending, until a column header is clicked. */
const nameSort = (nameField: string): ITableSort => ({
  sortField: nameField,
  sortOrder: 1,
});

function Muted({ children }: { children: ReactNode }) {
  return <span className="text-color-secondary">{children}</span>;
}

/** A value range, with "no value" in the muted colour. */
function ValueRange({
  min,
  max,
  benefit,
  hadNoValue = false,
}: {
  min: number | null;
  max: number | null;
  benefit: RemitScoredBenefit;
  hadNoValue?: boolean;
}) {
  const range = formatRange(min, max, benefit);
  if (!range && !hadNoValue) return <span>—</span>;
  return (
    <span>
      {range}
      {range && hadNoValue ? " / " : ""}
      {hadNoValue && <Muted>no value</Muted>}
    </span>
  );
}

function SectionHeader({
  title,
  subtitle,
  aside,
}: {
  title: string;
  subtitle?: ReactNode;
  aside?: ReactNode;
}) {
  return (
    <div className="flex flex-wrap justify-content-between align-items-end gap-2 mt-5 mb-3">
      <div className="flex flex-column gap-2">
        <h3 className="m-0">{title}</h3>
        {subtitle && <Muted>{subtitle}</Muted>}
      </div>
      {aside && <Muted>{aside}</Muted>}
    </div>
  );
}

function AccuracyBar({
  cell,
}: {
  cell: Pick<IRemitHeadlineCell, "scored" | "match">;
}) {
  return (
    <div className="flex align-items-center gap-2">
      <ProgressBar
        value={percentOf(cell) ?? 0}
        showValue={false}
        style={{ width: "7rem", height: "0.4rem" }}
      />
      <span>{accuracyText(cell)}</span>
    </div>
  );
}

function TileBody({
  benefit,
  kind,
  cells,
  empty,
}: {
  benefit: RemitScoredBenefit;
  kind: RemitPickKind;
  cells: IRemitHeadlineCell[];
  empty: IRemitEmptyContext;
}) {
  const cell = cellFor(cells, kind, benefit);
  if (kindIsEmpty(cells, kind)) {
    return (
      <span className="text-xl font-semibold text-color-secondary">
        {emptyStateText(empty)}
      </span>
    );
  }
  if (!cell || cell.scored === 0) {
    return (
      <span className="text-2xl font-semibold text-color-secondary">
        Not measured · 0 checks
      </span>
    );
  }
  return (
    <>
      <div className="flex align-items-baseline gap-2">
        <span className="text-4xl font-semibold">
          {(percentOf(cell) as number).toFixed(1)}%
        </span>
        <Muted>
          {cell.match.toLocaleString()} of {cell.scored.toLocaleString()} checks
        </Muted>
      </div>
      <ProgressBar
        value={percentOf(cell) ?? 0}
        showValue={false}
        style={{ height: "0.5rem" }}
      />
      {benefit !== "COINSURANCE_PERCENT" && (
        <Muted>
          {cell.scoredZeroTruth.toLocaleString()} confirmed $0 ·{" "}
          {cell.scoredPositiveTruth.toLocaleString()} confirmed above $0
        </Muted>
      )}
    </>
  );
}

const AGREEMENT_FIELD: Record<
  RemitAiAgreement,
  "bothRight" | "onlyAi" | "onlyEngine" | "bothWrong"
> = {
  BOTH_RIGHT: "bothRight",
  ONLY_AI: "onlyAi",
  ONLY_ENGINE: "onlyEngine",
  BOTH_WRONG: "bothWrong",
};

/** CLE-2820 — under a tile: the AI against today's config on the same checks, each count opening those checks. */
function AiSplit({
  benefit,
  cell,
  onOpen,
}: {
  benefit: RemitScoredBenefit;
  cell?: IRemitAiAgreementCell;
  onOpen: (benefit: RemitScoredBenefit, agreement: RemitAiAgreement) => void;
}) {
  return (
    <div
      className="border-top-1 surface-border pt-2 mt-1 flex flex-column gap-1"
      data-testid={`remitAccuracy.aiSplit.${benefit}`}
    >
      <Muted>
        AI vs today&apos;s config ·{" "}
        {cell && cell.compared > 0
          ? `${cell.compared.toLocaleString()} checks`
          : "no checks compared yet"}
      </Muted>
      {cell && cell.compared > 0 && (
        <div className="flex flex-wrap gap-3">
          {AI_AGREEMENT_ORDER.map((agreement) => (
            <span key={agreement} className="flex align-items-center gap-1">
              <Button
                type="button"
                className="p-button-link p-0 font-semibold"
                label={cell[AGREEMENT_FIELD[agreement]].toLocaleString()}
                disabled={cell[AGREEMENT_FIELD[agreement]] === 0}
                onClick={() => onOpen(benefit, agreement)}
                data-testid={`remitAccuracy.aiSplit.${benefit}.${agreement}`}
              />
              <Muted>{AI_AGREEMENT_LABELS[agreement].toLowerCase()}</Muted>
            </span>
          ))}
        </div>
      )}
    </div>
  );
}

function HeadlineTile({
  benefit,
  kind,
  cells,
  empty,
  showOtherKind,
  aiSplit,
}: {
  benefit: RemitScoredBenefit;
  kind: RemitPickKind;
  cells: IRemitHeadlineCell[];
  empty: IRemitEmptyContext;
  /** False while "At time of check" has nothing scored: the tile then shows only today's config. */
  showOtherKind: boolean;
  /** CLE-2820 — rendered under the tile only while "Include AI comparison" is checked. */
  aiSplit?: ReactNode;
}) {
  const other = OTHER_KIND[kind];
  const otherText = kindIsEmpty(cells, other)
    ? emptyStateText(empty)
    : accuracyText(cellFor(cells, other, benefit));

  return (
    <div
      className="border-1 surface-border border-round p-3 h-full flex flex-column gap-2"
      data-testid={`remitAccuracy.tile.${benefit}`}
    >
      <span className="font-semibold text-color-secondary">
        {REMIT_BENEFIT_LABELS[benefit]}
      </span>
      <TileBody benefit={benefit} kind={kind} cells={cells} empty={empty} />
      {showOtherKind && (
        <Muted>
          {REMIT_KIND_LABELS[other]}: {otherText}
        </Muted>
      )}
      {aiSplit}
    </div>
  );
}

const disagreementNameBody = (row: IRemitAiDisagreementCluster) => (
  <div className="flex flex-column gap-1">
    <span className="font-semibold">
      {row.accountName} · {row.payerName}
    </span>
    <Muted>{row.planName ?? "No plan name"}</Muted>
  </div>
);

const disagreementEngineBody = (row: IRemitAiDisagreementCluster) => (
  <ValueRange min={row.engineMin} max={row.engineMax} benefit={row.benefit} />
);

const disagreementAiBody = (row: IRemitAiDisagreementCluster) => (
  <ValueRange min={row.aiMin} max={row.aiMax} benefit={row.benefit} />
);

const disagreementRemitBody = (row: IRemitAiDisagreementCluster) => (
  <ValueRange min={row.remitMin} max={row.remitMax} benefit={row.benefit} />
);

/** CLE-2820 — how often the AI is right at each confidence, per benefit. */
function ConfidenceTable({ ai }: { ai: IRemitAiSummary }) {
  const bands = ["HIGH", "MEDIUM", "LOW"] as const;
  const cellBody = (
    band: (typeof bands)[number],
    benefit: RemitScoredBenefit
  ) => {
    const found = ai.confidenceBands.find(
      (each) => each.band === band && each.benefit === benefit
    );
    return found && found.scored > 0 ? (
      <AccuracyBar cell={found} />
    ) : (
      <Muted>No checks</Muted>
    );
  };
  return (
    <DataTable
      value={bands.map((band) => ({ band }))}
      dataKey="band"
      data-testid="remitAccuracy.aiConfidence"
    >
      <Column
        header="AI confidence"
        body={(row: { band: (typeof bands)[number] }) =>
          CONFIDENCE_BAND_LABELS[row.band]
        }
      />
      <Column
        header="Co-insurance"
        body={(row: { band: (typeof bands)[number] }) =>
          cellBody(row.band, "COINSURANCE_PERCENT")
        }
      />
      <Column
        header="Copay"
        body={(row: { band: (typeof bands)[number] }) =>
          cellBody(row.band, "FLAT_COPAY")
        }
      />
      <Column
        header="Physician copay"
        body={(row: { band: (typeof bands)[number] }) =>
          cellBody(row.band, "PROFESSIONAL_COPAY")
        }
      />
    </DataTable>
  );
}

/** One benefit's cell in the by-account table: the numbers first, then the reason there are none. */
function AccountCell({
  account,
  kind,
  benefit,
}: {
  account: IRemitAccuracyAccount;
  kind: RemitPickKind;
  benefit: RemitScoredBenefit;
}) {
  const cell = cellFor(account.cells, kind, benefit);
  // What was scored comes first: an account whose map rows were all retired still has its counts.
  if (cell && cell.scored > 0) return <AccuracyBar cell={cell} />;
  if (cell && cell.notConfiguredWithCopay > 0)
    return (
      <span className="text-orange-700">
        Not configured · {cell.notConfiguredWithCopay} checks{" "}
        {remitShareWording(benefit)}
      </span>
    );
  if (cell && cell.notConfigured > 0)
    return <span className="text-orange-700">Not configured</span>;
  if (account.linkedClaims === 0)
    return <Muted>Not measured · no linked remits</Muted>;
  if (!account.configured)
    return <Muted>Not measured · no benefit configuration</Muted>;
  return <Muted>Not measured</Muted>;
}

function PayerCell({
  payer,
  kind,
  benefit,
}: {
  payer: IRemitAccuracyPayer;
  kind: RemitPickKind;
  benefit: RemitScoredBenefit;
}) {
  const cell = cellFor(payer.cells, kind, benefit);
  if (cell && cell.scored > 0) return <AccuracyBar cell={cell} />;
  if (cell && cell.notConfigured > 0)
    return <span className="text-orange-700">Not configured</span>;
  return <Muted>Not measured</Muted>;
}

/**
 * CLE-2818 — REMIT ACCURACY: how often the benefit engine's pick agrees with what the payer applied on the
 * 835 remit. Internal, SuperAdmin only; rebuilt nightly. A percentage is never shown without its sample
 * size, and an account with nothing to measure reads "not measured", never a low score.
 */
export function RemitAccuracyTab() {
  const [params, setParams] = useSearchParams();
  const [excludedReason, setExcludedReason] = useState<string | null>(null);
  const [aiChecks, setAiChecks] = useState<{
    benefit: RemitScoredBenefit;
    agreement: RemitAiAgreement;
  } | null>(null);
  const [accountSort, setAccountSort] = useState(nameSort("accountName"));
  const [payerSort, setPayerSort] = useState(nameSort("payerName"));

  const accountId = params.get(PARAM.account) ?? undefined;
  const payerId = params.get(PARAM.payer) ?? undefined;
  const benefit = oneOf(params.get(PARAM.benefit), BENEFITS);
  const dateOfService: DateOfServicePreset =
    oneOf(
      params.get(PARAM.dateOfService),
      DATE_OF_SERVICE_OPTIONS.map((option) => option.value)
    ) ?? "ALL";

  /** Sets or clears params. Filters replace the history entry; opening or closing a cluster pushes one. */
  const update = (changes: Record<string, string | undefined>, push = false) =>
    setParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        Object.entries(changes).forEach(([key, value]) => {
          if (value === undefined) next.delete(key);
          else next.set(key, value);
        });
        return next;
      },
      { replace: !push }
    );

  const filters: IRemitAccuracyFilters = useMemo(
    () => ({
      accountId,
      payerId,
      benefit,
      ...dateOfServiceRange(dateOfService),
    }),
    [accountId, payerId, benefit, dateOfService]
  );
  // CLE-2820 — unchecked, the request is exactly the filters it always was; the API is asked for AI data only when checked.
  const includeAi = params.get(PARAM.ai) === "1";
  const summaryRequest = useMemo(
    () => (includeAi ? { ...filters, includeAi: true } : filters),
    [filters, includeAi]
  );
  const { data, isLoading, isError } =
    useGetRemitAccuracySummaryQuery(summaryRequest);
  const summary = data?.data;
  const ai = includeAi ? summary?.ai : undefined;
  // CLE-2818 item 11 — "At time of check" stays hidden, toggle and all, until one of its picks has been
  // scored; it reappears on its own when the data does. A `?pick=quoted` link meanwhile falls back to today's config.
  const quotedAvailable = quotedIsAvailable(summary?.quoted);
  const kind: RemitPickKind =
    quotedAvailable && params.get(PARAM.pick) === "quoted"
      ? "QUOTED"
      : "REPLAY";
  const cells = summary?.headline ?? [];
  const benefits = benefit ? [benefit] : BENEFITS;
  const empty: IRemitEmptyContext = {
    lastUpdatedAt: summary?.lastUpdatedAt ?? null,
  };

  const clusterBenefit = oneOf(params.get(PARAM.clusterBenefit), BENEFITS);
  const clusterAccount = params.get(PARAM.clusterAccount);
  const clusterReadFrom = params.get(PARAM.clusterReadFrom);
  if (clusterAccount && clusterBenefit && clusterReadFrom) {
    // The engine pick depends on the summary (item 11). Until it loads, request nothing, so a deep-linked
    // at-time-of-check cluster is not fetched once as today's config first.
    if (!summary) {
      return (
        <Surface data-testid="remitAccuracyTab">
          {isError ? (
            <Message
              severity="error"
              text="Remit accuracy could not be loaded."
            />
          ) : (
            <Muted>Loading…</Muted>
          )}
        </Surface>
      );
    }
    const cluster: IRemitClusterRequest = {
      ...filters,
      kind,
      accountId: clusterAccount,
      payerId: params.get(PARAM.clusterPayer) ?? "none",
      ...(params.get(PARAM.clusterNoPlan) === "1"
        ? { planNameMissing: true }
        : { planName: params.get(PARAM.clusterPlan) ?? undefined }),
      benefit: clusterBenefit,
      readFrom: clusterReadFrom,
      ...(includeAi ? { includeAi: true } : {}),
    };
    return (
      <Surface data-testid="remitAccuracyTab">
        <RemitAccuracyCluster
          request={cluster}
          onBack={() =>
            update(
              Object.fromEntries(CLUSTER_PARAMS.map((key) => [key, undefined])),
              true
            )
          }
        />
      </Surface>
    );
  }

  const emptyText = emptyStateText(empty);
  const kindEmpty = kindIsEmpty(cells, kind);
  const callouts = (summary?.notConfiguredWithCopays ?? []).filter(
    (callout) => callout.kind === kind
  );
  const clusters = (summary?.missClusters ?? []).filter(
    (each) => each.kind === kind
  );
  // Only rows with something scored for the chosen pick; the tables sort them (item 1 of round 3).
  const accounts = withMeasurement(summary?.accounts ?? [], kind);
  const payers = withMeasurement(summary?.payers ?? [], kind);
  const exclusions = visibleExclusions(summary?.exclusions);
  const totalMisses = cells
    .filter((cell) => cell.kind === kind)
    .reduce((sum, cell) => sum + cell.scored - cell.match, 0);

  const sortAccounts = (event: { data: IRemitAccuracyAccount[] }) =>
    sortRemitRows(
      event.data,
      accountSort.sortField,
      accountSort.sortOrder,
      kind,
      (row) => row.accountName
    );
  const sortPayers = (event: { data: IRemitAccuracyPayer[] }) =>
    sortRemitRows(
      event.data,
      payerSort.sortField,
      payerSort.sortOrder,
      kind,
      (row) => row.payerName
    );
  const onSortChange =
    (set: (sort: ITableSort) => void) =>
    (event: { sortField: string; sortOrder?: number | null }) =>
      set({
        sortField: event.sortField,
        sortOrder: event.sortOrder === -1 ? -1 : 1,
      });

  const configureBody = (callout: IRemitNotConfiguredCallout) => (
    <Link to={`/hospitals/edit/${callout.accountId}`}>Configure ›</Link>
  );

  const clusterNameBody = (row: IRemitMissCluster) => (
    <div className="flex flex-column gap-1">
      <span className="font-semibold">
        {row.accountName} · {row.payerName}
      </span>
      <Muted>{row.planName ?? "No plan name"}</Muted>
    </div>
  );

  const openClusterBody = (row: IRemitMissCluster) => (
    <Button
      type="button"
      label="Analyze"
      className="p-button-text p-button-sm"
      data-testid="remitAccuracy.cluster.open"
      onClick={() =>
        update(
          {
            [PARAM.clusterAccount]: row.accountId,
            [PARAM.clusterPayer]:
              row.payerId === null ? "none" : String(row.payerId),
            [PARAM.clusterPlan]: row.planName ?? undefined,
            [PARAM.clusterNoPlan]: row.planName === null ? "1" : undefined,
            [PARAM.clusterBenefit]: row.benefit,
            [PARAM.clusterReadFrom]: row.readFrom,
          },
          true
        )
      }
    />
  );

  const openDisagreementBody = (row: IRemitAiDisagreementCluster) => (
    <Button
      type="button"
      label="Analyze"
      className="p-button-text p-button-sm"
      data-testid="remitAccuracy.aiDisagreement.open"
      onClick={() =>
        update(
          {
            // The comparison is with today's config, so the cluster opens on it.
            [PARAM.pick]: undefined,
            [PARAM.clusterAccount]: row.accountId,
            [PARAM.clusterPayer]:
              row.payerId === null ? "none" : String(row.payerId),
            [PARAM.clusterPlan]: row.planName ?? undefined,
            [PARAM.clusterNoPlan]: row.planName === null ? "1" : undefined,
            [PARAM.clusterBenefit]: row.benefit,
            [PARAM.clusterReadFrom]: row.readFrom,
          },
          true
        )
      }
    />
  );

  const engineRangeBody = (row: IRemitMissCluster) => (
    <ValueRange
      min={row.engineMin}
      max={row.engineMax}
      benefit={row.benefit}
      hadNoValue={row.engineHadNoValue}
    />
  );
  const remitRangeBody = (row: IRemitMissCluster) => (
    <ValueRange min={row.remitMin} max={row.remitMax} benefit={row.benefit} />
  );

  const coinsuranceAccountBody = (account: IRemitAccuracyAccount) => (
    <AccountCell account={account} kind={kind} benefit="COINSURANCE_PERCENT" />
  );
  const copayAccountBody = (account: IRemitAccuracyAccount) => (
    <AccountCell account={account} kind={kind} benefit="FLAT_COPAY" />
  );
  const physicianAccountBody = (account: IRemitAccuracyAccount) => (
    <AccountCell account={account} kind={kind} benefit="PROFESSIONAL_COPAY" />
  );

  const coinsurancePayerBody = (payer: IRemitAccuracyPayer) => (
    <PayerCell payer={payer} kind={kind} benefit="COINSURANCE_PERCENT" />
  );
  const copayPayerBody = (payer: IRemitAccuracyPayer) => (
    <PayerCell payer={payer} kind={kind} benefit="FLAT_COPAY" />
  );
  const physicianPayerBody = (payer: IRemitAccuracyPayer) => (
    <PayerCell payer={payer} kind={kind} benefit="PROFESSIONAL_COPAY" />
  );

  return (
    <Surface data-testid="remitAccuracyTab">
      <div className="flex flex-wrap justify-content-between gap-2 mb-4">
        <Muted>
          How often the benefit engine&apos;s pick agrees with what the payer
          actually applied on the 835 remit.
        </Muted>
        <span
          className="text-color-secondary"
          data-testid="remitAccuracy.lastRun"
          title={
            summary?.lastUpdatedAt
              ? new Date(summary.lastUpdatedAt).toLocaleString()
              : undefined
          }
        >
          {summary?.lastUpdatedAt
            ? `Last updated ${relativeTime(summary.lastUpdatedAt)}`
            : "Not updated yet"}
        </span>
      </div>

      <div className="flex flex-wrap align-items-end gap-3 mb-4">
        <div className="flex flex-column gap-1">
          <label htmlFor="remitAccuracy.account">Account</label>
          <Dropdown
            inputId="remitAccuracy.account"
            value={accountId ?? null}
            options={summary?.options.accounts ?? []}
            optionLabel="accountName"
            optionValue="accountId"
            placeholder="All accounts"
            showClear
            filter
            className="w-16rem"
            onChange={(e) => update({ [PARAM.account]: e.value ?? undefined })}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="remitAccuracy.payer">Payer</label>
          <Dropdown
            inputId="remitAccuracy.payer"
            value={payerId ?? null}
            options={(summary?.options.payers ?? []).map((payer) => ({
              label: payer.payerName,
              value: String(payer.payerId),
            }))}
            placeholder="All payers"
            showClear
            filter
            className="w-14rem"
            onChange={(e) => update({ [PARAM.payer]: e.value ?? undefined })}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="remitAccuracy.benefit">Benefit</label>
          <Dropdown
            inputId="remitAccuracy.benefit"
            value={benefit ?? null}
            options={BENEFITS.map((each) => ({
              label: REMIT_BENEFIT_LABELS[each],
              value: each,
            }))}
            placeholder="All benefits"
            showClear
            className="w-12rem"
            onChange={(e) => update({ [PARAM.benefit]: e.value ?? undefined })}
          />
        </div>
        <div className="flex flex-column gap-1">
          <label htmlFor="remitAccuracy.dateOfService">Date of service</label>
          <Dropdown
            inputId="remitAccuracy.dateOfService"
            value={dateOfService}
            options={DATE_OF_SERVICE_OPTIONS}
            className="w-12rem"
            onChange={(e) =>
              update({
                [PARAM.dateOfService]: e.value === "ALL" ? undefined : e.value,
              })
            }
          />
        </div>
        <div className="flex align-items-center gap-2 pb-2">
          <Checkbox
            inputId="remitAccuracy.includeAi"
            checked={includeAi}
            onChange={(e) =>
              update({ [PARAM.ai]: e.checked ? "1" : undefined })
            }
            data-testid="remitAccuracy.includeAi"
          />
          <label htmlFor="remitAccuracy.includeAi">Include AI comparison</label>
        </div>
        {quotedAvailable && (
          <div className="flex flex-column gap-1 ml-auto">
            <span>Engine pick</span>
            <SelectButton
              value={kind}
              options={KINDS.map((value) => ({
                value,
                label: REMIT_KIND_LABELS[value],
              }))}
              onChange={(e) =>
                e.value &&
                update({
                  [PARAM.pick]: e.value === "QUOTED" ? "quoted" : undefined,
                })
              }
              data-testid="remitAccuracy.kind"
            />
          </div>
        )}
      </div>

      {isError && (
        <Message severity="error" text="Remit accuracy could not be loaded." />
      )}

      <div className="grid">
        {benefits.map((each) => (
          <div key={each} className="col-12 md:col-4">
            <HeadlineTile
              benefit={each}
              kind={kind}
              cells={cells}
              empty={empty}
              showOtherKind={quotedAvailable}
              aiSplit={
                includeAi ? (
                  <AiSplit
                    benefit={each}
                    cell={ai?.agreement.find((cell) => cell.benefit === each)}
                    onOpen={(selected, agreement) =>
                      setAiChecks({ benefit: selected, agreement })
                    }
                  />
                ) : undefined
              }
            />
          </div>
        ))}
      </div>

      {callouts.length > 0 && (
        <div
          id="remit-accuracy-not-configured"
          className="border-1 border-yellow-300 bg-yellow-50 border-round p-3 mt-5"
          data-testid="remitAccuracy.notConfigured"
        >
          <div className="flex flex-wrap align-items-center gap-2 mb-3">
            <i className="pi pi-exclamation-triangle text-yellow-700" />
            <span className="font-semibold">
              Not configured, but remits show copays
            </span>
            <Muted>
              The engine declines these today, so at cutover they fall back to
              legacy.
            </Muted>
          </div>
          <DataTable value={callouts} dataKey="accountId">
            <Column field="accountName" header="Account" />
            <Column
              header="Benefit"
              body={(row: IRemitNotConfiguredCallout) =>
                REMIT_BENEFIT_LABELS[row.benefit]
              }
            />
            <Column field="checksWithCopay" header="Checks with a copay" />
            <Column field="checksWithoutCopay" header="Checks without" />
            <Column
              header="Most common remit copay"
              body={(row: IRemitNotConfiguredCallout) =>
                formatRemitValue(row.mostCommonCopay, row.benefit)
              }
            />
            <Column header="" body={configureBody} />
          </DataTable>
        </div>
      )}

      <SectionHeader
        title="Biggest miss clusters"
        subtitle="Checks where the engine disagrees with the remit, grouped by account, payer, plan, benefit and where the engine read from. Ranked by misses."
        aside={`${totalMisses.toLocaleString()} misses in total`}
      />
      <DataTable
        value={clusters}
        loading={isLoading}
        emptyMessage={kindEmpty ? `${emptyText}.` : "No misses."}
        data-testid="remitAccuracy.missClusters"
      >
        <Column
          header="#"
          body={(_row, options) => options.rowIndex + 1}
          style={{ width: "3rem" }}
        />
        <Column header="Account · Payer · Plan" body={clusterNameBody} />
        <Column
          header="Benefit"
          body={(row: IRemitMissCluster) => REMIT_BENEFIT_LABELS[row.benefit]}
        />
        <Column
          header="Misses"
          body={(row: IRemitMissCluster) => `${row.misses} of ${row.checks}`}
        />
        <Column header="Engine" body={engineRangeBody} />
        <Column header="Remit" body={remitRangeBody} />
        <Column
          header="Engine read from"
          body={(row: IRemitMissCluster) =>
            READ_FROM_LABELS[row.readFrom] ?? row.readFrom
          }
        />
        <Column header="" body={openClusterBody} />
      </DataTable>

      {includeAi && (
        <>
          <SectionHeader
            title="Where AI and the engine disagree"
            subtitle="Checks where exactly one of the AI and today's config matches the remit, grouped like the miss clusters. Ranked by checks only the AI got right."
            aside={
              ai
                ? `${ai.checksAsked.toLocaleString()} checks asked${
                    ai.skipped.length
                      ? ` · not sent: ${ai.skipped
                          .map(
                            (each) =>
                              `${aiSkipReasonLabel(each.reason)} ${each.count}`
                          )
                          .join(", ")}`
                      : ""
                  }`
                : undefined
            }
          />
          <DataTable
            value={ai?.disagreementClusters ?? []}
            loading={isLoading}
            emptyMessage="No disagreements."
            data-testid="remitAccuracy.aiDisagreements"
          >
            <Column
              header="#"
              body={(_row, options) => options.rowIndex + 1}
              style={{ width: "3rem" }}
            />
            <Column
              header="Account · Payer · Plan"
              body={disagreementNameBody}
            />
            <Column
              header="Benefit"
              body={(row: IRemitAiDisagreementCluster) =>
                REMIT_BENEFIT_LABELS[row.benefit]
              }
            />
            <Column
              header="Only AI right"
              body={(row: IRemitAiDisagreementCluster) =>
                `${row.onlyAi} of ${row.compared}`
              }
            />
            <Column
              header="Only engine right"
              body={(row: IRemitAiDisagreementCluster) =>
                `${row.onlyEngine} of ${row.compared}`
              }
            />
            <Column header="Engine" body={disagreementEngineBody} />
            <Column header="AI" body={disagreementAiBody} />
            <Column header="Remit" body={disagreementRemitBody} />
            <Column
              header="Engine read from"
              body={(row: IRemitAiDisagreementCluster) =>
                READ_FROM_LABELS[row.readFrom] ?? row.readFrom
              }
            />
            <Column header="" body={openDisagreementBody} />
          </DataTable>

          <SectionHeader
            title="AI confidence vs accuracy"
            subtitle="How often the AI's pick matches the remit at each confidence it reported."
          />
          {ai ? (
            <ConfidenceTable ai={ai} />
          ) : (
            <Muted>{isLoading ? "Loading…" : "No AI data."}</Muted>
          )}
        </>
      )}

      <SectionHeader
        title="By account"
        subtitle="Accounts with at least one scored check under these filters. Click a column to sort; a benefit sorts by accuracy, with unmeasured accounts last."
      />
      <DataTable
        value={accounts}
        dataKey="accountId"
        loading={isLoading}
        paginator
        rows={20}
        sortField={accountSort.sortField}
        sortOrder={accountSort.sortOrder}
        onSort={onSortChange(setAccountSort)}
        emptyMessage={`${emptyText}.`}
        data-testid="remitAccuracy.accounts"
      >
        <Column
          field="accountName"
          header="Account"
          sortable
          sortFunction={sortAccounts}
        />
        <Column
          field="COINSURANCE_PERCENT"
          header="Co-insurance"
          body={coinsuranceAccountBody}
          sortable
          sortFunction={sortAccounts}
        />
        <Column
          field="FLAT_COPAY"
          header="Copay"
          body={copayAccountBody}
          sortable
          sortFunction={sortAccounts}
        />
        <Column
          field="PROFESSIONAL_COPAY"
          header="Physician copay"
          body={physicianAccountBody}
          sortable
          sortFunction={sortAccounts}
        />
      </DataTable>

      <SectionHeader
        title="By payer"
        subtitle="Payers with at least one scored check under these filters. Click a column to sort; a benefit sorts by accuracy, with unmeasured payers last."
      />
      <DataTable
        value={kindEmpty ? [] : payers}
        dataKey="payerId"
        loading={isLoading}
        paginator
        rows={20}
        sortField={payerSort.sortField}
        sortOrder={payerSort.sortOrder}
        onSort={onSortChange(setPayerSort)}
        emptyMessage={`${emptyText}.`}
        data-testid="remitAccuracy.payers"
      >
        <Column
          field="payerName"
          header="Payer"
          sortable
          sortFunction={sortPayers}
        />
        <Column
          field="COINSURANCE_PERCENT"
          header="Co-insurance"
          body={coinsurancePayerBody}
          sortable
          sortFunction={sortPayers}
        />
        <Column
          field="FLAT_COPAY"
          header="Copay"
          body={copayPayerBody}
          sortable
          sortFunction={sortPayers}
        />
        <Column
          field="PROFESSIONAL_COPAY"
          header="Physician copay"
          body={physicianPayerBody}
          sortable
          sortFunction={sortPayers}
        />
      </DataTable>

      <div
        className="border-1 surface-border border-round p-3 mt-5"
        data-testid="remitAccuracy.excluded"
      >
        <div className="flex flex-column gap-2 mb-3">
          <h3 className="m-0">Remits not scored</h3>
          <Muted>
            {exclusions.notScored.toLocaleString()} of{" "}
            {exclusions.remits.toLocaleString()}. Each one is excluded because
            its remit can&apos;t be trusted as the answer for that check.
          </Muted>
        </div>
        <div className="grid">
          {exclusions.reasons.map((each) => (
            <div key={each.reason} className="col-12 md:col-3">
              <div
                className="border-1 surface-border border-round p-3 h-full flex flex-column align-items-center justify-content-center text-center gap-1"
                data-testid={`remitAccuracy.excluded.${each.reason}`}
              >
                <Button
                  type="button"
                  className="p-button-text p-0 text-4xl font-bold"
                  label={each.count.toLocaleString()}
                  onClick={() => setExcludedReason(each.reason)}
                />
                <Muted>{exclusionLabel(each.reason)}</Muted>
              </div>
            </div>
          ))}
        </div>
        <span className="text-xs text-color-secondary">
          Remits with no patient share can&apos;t confirm a copay or a
          co-insurance rate, and aren&apos;t counted as misses.
        </span>
      </div>

      <RemitAccuracyExcludedDialog
        reason={excludedReason}
        filters={filters}
        onHide={() => setExcludedReason(null)}
      />
      {includeAi && (
        <RemitAccuracyAiChecksDialog
          selection={aiChecks}
          filters={filters}
          onHide={() => setAiChecks(null)}
        />
      )}
    </Surface>
  );
}
